import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../axiosBaseQuery";
import type { Response } from "../interfaces";

export interface ChatBranding {
  chat_branding_id: string;
  title: string | null;
  greet_message: string;
  universal_greet_message: string | null;
  greet_message_text_color: string;
  greet_button_text: string;
  gift_box_class: string;
  button_background_color: string;
  button_text_color: string;
  bubble_background_color: string;
  bubble_text_color: string;
  bubble_button_background_color: string;
  bubble_button_text_color: string;
  background: string;
  logo: string;
  gift_box_image: string;
  side_image: string;
  is_delete: boolean;
  organization_id?: string | null;
  organization_name?: string | null;
  contact_email?: string | null;
  added_on?: string;
  updated_on?: string;
}

export interface CreateChatBrandingDto {
  title?: string;
  greet_message: string;
  universal_greet_message?: string;
  greet_message_text_color?: string;
  greet_button_text?: string;
  gift_box_class?: string;
  button_background_color?: string;
  button_text_color?: string;
  bubble_background_color?: string;
  bubble_text_color?: string;
  bubble_button_background_color?: string;
  bubble_button_text_color?: string;
  background?: string;
  logo?: string;
  gift_box_image?: string;
  side_image?: string;
}

export interface UpdateChatBrandingDto extends Partial<CreateChatBrandingDto> { }

export const chatBrandingApi = createApi({
  reducerPath: "chatBrandingApi",
  baseQuery: axiosBaseQuery(),
  tagTypes: ["ChatBranding"],
  endpoints: (builder) => ({
    getChatBrandings: builder.query<Response<ChatBranding[]>, void>({
      query: () => ({
        url: "chat-branding",
        method: "GET",
      }),
      transformResponse: (response: unknown) => {
        if (Array.isArray(response)) {
          return { success: true, code: 200, message: "", data: response };
        }
        return response as Response<ChatBranding[]>;
      },
      providesTags: ["ChatBranding"],
    }),

    getChatBranding: builder.query<Response<ChatBranding>, string>({
      query: (id) => ({
        url: `chat-branding/${id}`,
        method: "GET",
      }),
      transformResponse: (response: unknown) => {
        const r = response as { data?: ChatBranding } | ChatBranding;
        if (r && typeof r === "object") {
          if ("data" in r && r.data && typeof r.data === "object") {
            return { success: true, code: 200, message: "", data: r.data };
          }
          if ("chat_branding_id" in r) {
            return { success: true, code: 200, message: "", data: r as ChatBranding };
          }
        }
        return { success: true, code: 200, message: "", data: null as unknown as ChatBranding };
      },
      providesTags: (_result, _error, id) => [{ type: "ChatBranding", id }],
    }),

    createChatBranding: builder.mutation<
      Response<ChatBranding>,
      CreateChatBrandingDto
    >({
      query: (data) => ({
        url: "chat-branding",
        method: "POST",
        data,
      }),
      invalidatesTags: ["ChatBranding"],
    }),

    updateChatBranding: builder.mutation<
      Response<ChatBranding>,
      { id: string; data: UpdateChatBrandingDto }
    >({
      query: ({ id, data }) => ({
        url: `chat-branding/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "ChatBranding", id },
        "ChatBranding",
      ],
    }),

    deleteChatBranding: builder.mutation<Response<void>, string>({
      query: (id) => ({
        url: `chat-branding/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["ChatBranding"],
    }),

    cloneChatBranding: builder.mutation<Response<ChatBranding>, { id: string; title?: string }>({
      query: ({ id, title }) => ({
        url: `chat-branding/${id}/clone`,
        method: "POST",
        data: title != null ? { title } : undefined,
      }),
      invalidatesTags: ["ChatBranding"],
    }),

    mapChatBrandingOrganization: builder.mutation<
      Response<{
        chat_branding_id: string;
        organization_id: string;
        organization_name: string;
        contact_email: string;
      }>,
      { id: string; contact_email: string }
    >({
      query: ({ id, contact_email }) => ({
        url: `chat-branding/${id}/map-organization`,
        method: "POST",
        data: { contact_email },
      }),
      invalidatesTags: ["ChatBranding"],
    }),
  }),
});

export const {
  useGetChatBrandingsQuery,
  useGetChatBrandingQuery,
  useCreateChatBrandingMutation,
  useUpdateChatBrandingMutation,
  useDeleteChatBrandingMutation,
  useCloneChatBrandingMutation,
  useMapChatBrandingOrganizationMutation,
} = chatBrandingApi;
